/**
 * Types related to role-based access control
 */

import { RoleType, PacketStatus } from './enums';
import { User } from './user';

export enum PacketOperation {
  VIEW = 'VIEW',
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
  TRANSITION_STATUS = 'TRANSITION_STATUS',
  ASSIGN_ROLES = 'ASSIGN_ROLES'
}

export interface PacketPermission {
  roleType: RoleType;
  operation: PacketOperation;
  allowedStatuses?: PacketStatus[] | 'all';
  restrictive?: boolean;
}

/**
 * Extended permission context for role-specific restrictions
 */
export interface PermissionContext {
  user: User;
  packet: { id: string; status: PacketStatus };
  operation: PacketOperation;
  requiredRoles?: RoleType[];
  allowedStatuses?: PacketStatus[];
  enforceRoleRestrictions?: RoleType[];
}

/**
 * Permission rules for each role type
 */
export const PACKET_PERMISSIONS: PacketPermission[] = [
  // CANDIDATE permissions
  {
    roleType: RoleType.CANDIDATE,
    operation: PacketOperation.VIEW,
    allowedStatuses: 'all'
  },
  {
    roleType: RoleType.CANDIDATE,
    operation: PacketOperation.CREATE,
    allowedStatuses: 'all'
  },
  {
    roleType: RoleType.CANDIDATE,
    operation: PacketOperation.UPDATE,
    allowedStatuses: 'all'
  },

  // ENDORSER permissions
  {
    roleType: RoleType.ENDORSER,
    operation: PacketOperation.VIEW,
    allowedStatuses: 'all'
  },
  {
    roleType: RoleType.ENDORSER,
    operation: PacketOperation.CREATE,
    allowedStatuses: 'all'
  },
  {
    roleType: RoleType.ENDORSER,
    operation: PacketOperation.UPDATE,
    allowedStatuses: 'all'
  },

  // MANAGER permissions
  {
    roleType: RoleType.MANAGER,
    operation: PacketOperation.VIEW,
    allowedStatuses: 'all'
  },
  {
    roleType: RoleType.MANAGER,
    operation: PacketOperation.CREATE,
    allowedStatuses: 'all'
  },
  {
    roleType: RoleType.MANAGER,
    operation: PacketOperation.UPDATE,
    allowedStatuses: 'all'
  },
  {
    roleType: RoleType.MANAGER,
    operation: PacketOperation.TRANSITION_STATUS,
    allowedStatuses: 'all'
  },
  {
    roleType: RoleType.MANAGER,
    operation: PacketOperation.DELETE,
    allowedStatuses: 'all'
  },

  // ASSESSOR permissions - marked as restrictive to prevent bypass
  {
    roleType: RoleType.ASSESSOR,
    operation: PacketOperation.VIEW,
    allowedStatuses: [
      PacketStatus.DRAFT, // Assessors can view DRAFT packets (with redaction)
      PacketStatus.BLINDED_ASSESSMENT,
      PacketStatus.UNBLINDED_ASSESSMENT,
      PacketStatus.MANAGER_SUMMARY,
      PacketStatus.SENIOR_LEADER_REVIEW,
      PacketStatus.HR_REVIEW,
      PacketStatus.COMPLETED
    ],
    restrictive: true
  },
  // BAR_RAISER permissions (same as ASSESSOR)
  {
    roleType: RoleType.BAR_RAISER,
    operation: PacketOperation.VIEW,
    allowedStatuses: [
      PacketStatus.DRAFT,
      PacketStatus.BLINDED_ASSESSMENT,
      PacketStatus.UNBLINDED_ASSESSMENT,
      PacketStatus.MANAGER_SUMMARY,
      PacketStatus.SENIOR_LEADER_REVIEW,
      PacketStatus.HR_REVIEW,
      PacketStatus.COMPLETED
    ],
    restrictive: true
  },
  {
    roleType: RoleType.BAR_RAISER,
    operation: PacketOperation.UPDATE,
    allowedStatuses: [
      PacketStatus.BLINDED_ASSESSMENT,
      PacketStatus.UNBLINDED_ASSESSMENT,
      PacketStatus.MANAGER_SUMMARY,
      PacketStatus.SENIOR_LEADER_REVIEW,
      PacketStatus.HR_REVIEW
    ],
    restrictive: true
  },
  {
    roleType: RoleType.ASSESSOR,
    operation: PacketOperation.UPDATE,
    allowedStatuses: [
      PacketStatus.BLINDED_ASSESSMENT,
      PacketStatus.UNBLINDED_ASSESSMENT
    ],
    restrictive: true
  },

  // SENIOR_LEADER permissions (full CRUD)
  {
    roleType: RoleType.SENIOR_LEADER,
    operation: PacketOperation.VIEW,
    allowedStatuses: 'all'
  },
  {
    roleType: RoleType.SENIOR_LEADER,
    operation: PacketOperation.CREATE,
    allowedStatuses: 'all'
  },
  {
    roleType: RoleType.SENIOR_LEADER,
    operation: PacketOperation.UPDATE,
    allowedStatuses: 'all'
  },
  {
    roleType: RoleType.SENIOR_LEADER,
    operation: PacketOperation.TRANSITION_STATUS,
    allowedStatuses: 'all'
  },
  {
    roleType: RoleType.SENIOR_LEADER,
    operation: PacketOperation.DELETE,
    allowedStatuses: [PacketStatus.DRAFT]
  },

  // HR permissions
  {
    roleType: RoleType.HR,
    operation: PacketOperation.VIEW,
    allowedStatuses: 'all'
  }
]; 